import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Capacitor } from '@capacitor/core';
import { firstValueFrom, fromEvent } from 'rxjs';
import { filter, first, timeout } from 'rxjs/operators';

import { FrontendBookmarkItem } from '@reach/interfaces/bookmarks/bookmarks.interface';

import { BookmarkItem, BookmarksState } from '../types';
import { transformBookmarks } from '../utils';

@Injectable()
export class BookmarksService {
	private readonly destroyRef = inject(DestroyRef);
	public async load(): Promise<{ state: BookmarksState; bookmarks: BookmarkItem[] }> {
		try {
			if (Capacitor.isNativePlatform()) {
				return {
					state: BookmarksState.IS_NATIVE,
					bookmarks: [],
				};
			}

			window.postMessage({ key: 'GET_BOOKMARKS' }, window.location.href);

			const eventObs$ = await fromEvent<
				MessageEvent<{ key: string; data: { bookmarks: FrontendBookmarkItem[] } }>
			>(window, 'message').pipe(
				filter((event) => {
					try {
						const { key, data } = event.data as {
							key: string;
							data: { bookmarks: FrontendBookmarkItem[] };
						};

						return key === 'USER_BOOKMARKS' && Array.isArray(data.bookmarks);
					} catch (error) {
						return false;
					}
				}),
				timeout(2_000),
				first(),
				takeUntilDestroyed(this.destroyRef)
			);

			const event = await firstValueFrom(eventObs$);

			const bookmarks = transformBookmarks(event.data.data.bookmarks);

			const state =
				bookmarks.length === 0 ? BookmarksState.EMPTY : BookmarksState.SHOWING_BOOKMARKS;

			return {
				state,
				bookmarks: bookmarks,
			};
		} catch (error) {
			console.error(error);
			return {
				state: BookmarksState.PLUGIN_NOT_INSTALLED,
				bookmarks: [],
			};
		}
	}
}
